import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Call } from '../../api/telemetry'
import { OutcomeBadge } from '../shared/OutcomeBadge'
import { cn } from '../../lib/utils'

export function CallInsightsPanel({ call }: { call: Call }) {
  const { t } = useTranslation('calls')

  // Inbound Retell receptionist calls carry provider-native routing analysis
  // instead of lead-gen scoring. Render that section instead of the generic
  // interest/outcome block (mutually exclusive — never both).
  const pa = call.provider_analysis
  const hasProviderAnalysis =
    !!pa &&
    (!!pa.caller_name ||
      !!pa.target_requested ||
      !!pa.reason_for_call ||
      !!pa.call_outcome)

  if (hasProviderAnalysis) {
    return <ProviderAnalysisPanel call={call} />
  }

  const level = typeof call.interest_level === 'number' ? call.interest_level : null
  const hasAny =
    level !== null ||
    !!call.outcome ||
    !!call.interest_reasoning ||
    !!call.inbound_reason

  if (!hasAny) {
    return (
      <div className="rounded-xl border border-border bg-surface-card p-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-text-muted" />
          <h3 className="text-base font-semibold text-text-primary">
            {t('detail.insights.title')}
          </h3>
        </div>
        <p className="mt-2 text-sm text-text-muted">{t('detail.insights.empty')}</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-surface-card p-5">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="text-base font-semibold text-text-primary">
          {t('detail.insights.title')}
        </h3>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-[auto_1fr]">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            {t('detail.insights.interest')}
          </p>
          <InterestScore level={level} />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            {t('detail.insights.outcome')}
          </p>
          <div className="mt-2">
            <OutcomeBadge outcome={call.outcome ?? null} />
          </div>
        </div>
      </div>

      {call.interest_reasoning && (
        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            {t('detail.insights.reasoning')}
          </p>
          <p className="mt-1 text-sm text-text-primary">{call.interest_reasoning}</p>
        </div>
      )}

      {call.inbound_reason && (
        <div className="mt-4 flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
            {t('detail.insights.inboundReason')}
          </span>
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {t(`detail.insights.inboundReasons.${call.inbound_reason}`, {
              defaultValue: call.inbound_reason,
            })}
          </span>
        </div>
      )}
    </div>
  )
}

function ProviderAnalysisPanel({ call }: { call: Call }) {
  const { t } = useTranslation('calls')
  const pa = call.provider_analysis ?? {}
  return (
    <div className="rounded-xl border border-border bg-surface-card p-5">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="text-base font-semibold text-text-primary">
          {t('detail.insights.title')}
        </h3>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {pa.caller_name && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
              {t('detail.insights.receptionist.callerName')}
            </p>
            <p className="mt-1 text-sm text-text-primary">{pa.caller_name}</p>
          </div>
        )}
        {pa.target_requested && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
              {t('detail.insights.receptionist.targetRequested')}
            </p>
            <p className="mt-1 text-sm text-text-primary">{pa.target_requested}</p>
          </div>
        )}
      </div>

      {pa.reason_for_call && (
        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            {t('detail.insights.receptionist.reasonForCall')}
          </p>
          <p className="mt-1 text-sm text-text-primary">{pa.reason_for_call}</p>
        </div>
      )}

      {pa.call_outcome && (
        <div className="mt-4 flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
            {t('detail.insights.receptionist.callOutcome')}
          </span>
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {t(`detail.insights.receptionist.outcomes.${pa.call_outcome}`, {
              defaultValue: pa.call_outcome,
            })}
          </span>
        </div>
      )}
    </div>
  )
}

function InterestScore({ level }: { level: number | null }) {
  if (level === null) {
    return <p className="mt-2 text-sm text-text-muted">—</p>
  }
  const clamped = Math.max(1, Math.min(5, Math.round(level)))
  const palette = ['bg-error', 'bg-warning', 'bg-text-muted', 'bg-primary', 'bg-success']
  const activeColor = palette[clamped - 1] ?? 'bg-primary'
  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            className={cn(
              'h-2.5 w-6 rounded-full',
              n <= clamped ? activeColor : 'bg-border',
            )}
          />
        ))}
      </div>
      <span className="text-sm font-semibold text-text-primary">{clamped}/5</span>
    </div>
  )
}
